import { Outlet, useNavigate, useLocation, matchPath, generatePath } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Button, Container, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { observer } from 'mobx-react-lite';
// utils
import { isCapacitor } from '../utils/platform';

// ----------------------------------------------------------------------

const RootStyle = styled(Container)(({ theme }) => {
  const isMobile = isCapacitor();
  
  return {
    height: 'calc(var(--vh, 1vh) * 100)',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    // Разные отступы для мобильных и веб
    paddingTop: isMobile 
      ? `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(2)})`
      : theme.spacing(4),
    paddingBottom: isMobile
      ? `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(2)})`
      : `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(4)})`,
    paddingLeft: isMobile ? theme.spacing(4) : theme.spacing(4),
    paddingRight: isMobile ? theme.spacing(4) : theme.spacing(4),
    backgroundColor: theme.palette.background.default
  };
});

// Маппинг путей к заголовкам страниц
const PAGE_TITLES: Record<string, string> = {
  '/login': '',
  '/registration': 'Регистрация',
  '/personal-data': 'Персональные данные',
  '/skill-lvl': 'Уровень навыков',
  '/profile': 'Профиль',
  '/main': 'Главная',
  '/video-interview/candidate': 'Видео-интервью',
  '/video-interview/check-devices': 'Проверка устройств',
  '/interviews': 'Мои интервью',
  '/settings': 'Настройки',
  '/hr/new-company': 'Новая компания',
  '/hr/company': 'Управление вакансиями',
  '/hr/new-vacancy': 'Создание вакансии',
  '/chat': 'Личные сообщения',
};

const PAGE_BACK_LINKS: Record<string, string | number> = {
  '/personal-data': '/main',
  '/skill-lvl': '/main',
  '/profile': '/main',
  '/video-interview/candidate': '/video-interview/check-devices',
  '/video-interview/check-devices': '/main',
  '/interviews': '/main',
  '/settings': '',
  '/login': '/',
  '/chat': '/main',
  '/hr/new-vacancy': -1,
};

function LogoOnlyLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleBack = () => {
    // 1) Точное совпадение
    if (PAGE_BACK_LINKS[location.pathname]) {
      navigate(PAGE_BACK_LINKS[location.pathname] as any);
      return;
    }

    // 2) Динамические маршруты
    const dynamicBackLinks: Array<{ pattern: string; to: string | number }> = [
      { pattern: '/requested-interviews/video-interview/record/:interviewId', to: '/main' },
      { pattern: '/video-interview/record/:interviewId', to: '/video-interview/check-devices' },
      { pattern: '/video-interview/:id/result', to: '/main' },
      { pattern: '/results/:id', to: '/interviews' },
      { pattern: '/skill-lvl/:id', to: '/main' },
      { pattern: '/hr/vacancy/:id', to: '/hr/company' },
      { pattern: '/hr/vacancy/:vacancyId/stats', to: '/hr/vacancy/:vacancyId' },
      { pattern: '/hr/vacancy/:vacancyId/candidate-result/:userId', to: '/hr/vacancy/:vacancyId' }
    ];

    for (const { pattern, to } of dynamicBackLinks) {
      const match = matchPath({ path: pattern, end: true }, location.pathname);
      if (match) {
        if (typeof to === 'string') {
          navigate(generatePath(to, match.params));
          return;
        }
        navigate(to as any);
        return;
      }
    }

    // 3) По умолчанию
    navigate('/main');
  };

  const getPageTitle = () => {
    // Проверяем точное совпадение пути
    if (PAGE_TITLES[location.pathname]) {
      return PAGE_TITLES[location.pathname];
    }

    // Проверяем динамические пути
    const dynamicTitles: Array<{ pattern: string; title: string }> = [
      { pattern: '/requested-interviews/video-interview/record/:interviewId', title: 'Запись интервью' },
      { pattern: '/video-interview/record/:interviewId', title: 'Запись интервью' },
      { pattern: '/video-interview/:id/result', title: '' },
      { pattern: '/results/:id', title: '' },
      { pattern: '/skill-lvl/:id', title: 'Уровень навыков' },
      { pattern: '/hr/vacancy/:id', title: 'Вакансия' },
      { pattern: '/hr/vacancy/:vacancyId/stats', title: 'Статистика по вакансии' },
      { pattern: '/requested-interviews/check-devices/:id', title: 'Проверка устройств' },
      { pattern: '/requested-interviews/vacancy-info/:id', title: 'Приглашение' },
      { pattern: '/hr/vacancy/:vacancyId/candidate-result/:userId', title: '' }
    ];

    for (const { pattern, title } of dynamicTitles) {
      if (matchPath({ path: pattern, end: true }, location.pathname)) {
        return title;
      }
    }

    return '';
  };

  const pageTitle = getPageTitle();
  const showBackButton = location.pathname !== '/main' && location.pathname !== '/' && !location.search?.includes('force=true');

  return (
    <RootStyle >
      {showBackButton && (
        <Stack
          sx={{
            mb: { xs: 4, md: 6, lg: 8 },
            position: 'relative',
            borderRadius: 2,
            // boxShadow: (theme) =>
            //   `0 6px 12px -2px ${alpha(theme.palette.grey[500], 0.24)}, 0 4px 6px -4px ${alpha(theme.palette.grey[500], 0.16)}`,
          }}
          direction="row"
          alignItems="center"
          spacing={6}
        >
          <Button sx={{ position: 'relative', zIndex: 100 }} startIcon={<Icon icon="oui:arrow-left" />} onClick={handleBack} size='small'>
            Назад
          </Button>
          {pageTitle && (
            <Typography
              variant="h5"
              fontWeight={700}
              textAlign="center"
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                maxWidth: '100%',
                width: '100%',
                margin: '0 auto !important',
                fontSize: { xs: 16, sm: 18, md: 20, lg: 24 },
              }}
            >
              {pageTitle}
            </Typography>
          )}
        </Stack>
      )}
      <Outlet />
    </RootStyle>
  );
}

export default observer(LogoOnlyLayout);
